import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { Button, Dialog, Icon, Typography } from '@equinor/eds-core-react'
import { close } from '@equinor/eds-icons'
import { baseFont } from '@/styles/layout'
import { useConfirmDialog } from '@/hooks'
import { dataUrlToBlob } from '../utils'

interface ImageProps {
    showImage: boolean
    addImage: (file: File) => void
    onClose: () => void
}

const TakePhotoOnboarding: React.FC<ImageProps> = ({ showImage, onClose, addImage }) => {
    const [photo, setPhoto] = useState<string | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [stream, setStream] = useState<MediaStream | null>(null)
    const [imageEdited, setImageEdited] = useState(false)

    const videoRef = useRef<HTMLVideoElement>(null)
    const canvasRef = useRef<HTMLCanvasElement>(null)

    const { showConfirm } = useConfirmDialog({
        title: 'Unsaved changes',
        content: 'You have unsaved changes. Are you sure you want to proceed?',
        rejectButtonTitle: 'Discard changes',
        confirmButtonTitle: 'Stay on page',
        onConfirm: () => {},
        onReject: () => {
            stopStream()
            onClose()
        },
        autoClose: true,
    })

    useEffect(() => {
        if (!showImage) return

        let cancelled = false

        const start = async () => {
            setError(null)
            setPhoto(null)
            setImageEdited(false)

            try {
                const s = await navigator.mediaDevices.getUserMedia({
                    video: { facingMode: { ideal: 'environment' } },
                    audio: false,
                })

                if (cancelled) {
                    s.getTracks().forEach((t) => t.stop())
                    return
                }

                setStream(s)

                if (videoRef.current) {
                    videoRef.current.srcObject = s
                    await videoRef.current.play()
                }
            } catch (e: any) {
                setError(
                    e?.name === 'NotAllowedError'
                        ? 'Camera permission was denied.'
                        : e?.name === 'NotFoundError'
                          ? 'No camera device found.'
                          : 'Could not open camera.'
                )
            }
        }

        start()

        return () => {
            cancelled = true
            setStream((prev) => {
                prev?.getTracks().forEach((t) => t.stop())
                return null
            })
        }
    }, [showImage])

    const stopStream = () => {
        stream?.getTracks().forEach((t) => t.stop())
        setStream(null)
    }

    const handleClose = () => {
        if (imageEdited) {
            showConfirm()
        } else {
            stopStream()
            onClose()
        }
    }

    const takePhoto = () => {
        const video = videoRef.current
        const canvas = canvasRef.current
        if (!video || !canvas) return

        const width = video.videoWidth
        const height = video.videoHeight
        if (!width || !height) return

        canvas.width = width
        canvas.height = height

        const ctx = canvas.getContext('2d')
        if (!ctx) return

        ctx.drawImage(video, 0, 0, width, height)
        const dataUrl = canvas.toDataURL('image/jpeg', 0.9)

        setPhoto(dataUrl)
        setImageEdited(true)
        stopStream()
    }

    const retake = async () => {
        setPhoto(null)
        setError(null)
        setImageEdited(false)

        try {
            const s = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: { ideal: 'environment' } },
                audio: false,
            })

            setStream(s)

            if (videoRef.current) {
                videoRef.current.srcObject = s
                await videoRef.current.play()
            }
        } catch (e: any) {
            setError(
                e?.name === 'NotAllowedError'
                    ? 'Camera permission was denied.'
                    : e?.name === 'NotFoundError'
                      ? 'No camera device found.'
                      : 'Could not open camera.'
            )
        }
    }

    const upload = async () => {
        if (!photo) return

        setError(null)
        const blob = dataUrlToBlob(photo)
        const file = new File([blob], 'photo.jpg', { type: blob.type })
        addImage(file)
        setImageEdited(false)
        onClose()
    }

    return (
        <ImageContainer color="black" open={showImage} onClose={handleClose} isDismissable>
            <Header>
                <HeaderLabel>Take photo</HeaderLabel>
                <CloseButton as="button" onClick={handleClose}>
                    <Icon data={close} size={24} style={{ color: '#007079' }} />
                </CloseButton>
            </Header>

            {error && <ErrorText>{error}</ErrorText>}

            {!error && !photo && <VideoView ref={videoRef} playsInline muted />}

            {photo && (
                <>
                    <ImageView src={photo} alt="Captured photo" />

                    <RowContainerBottom>
                        <Button as="button" onClick={retake} variant="outlined">
                            <Label>Retake</Label>
                        </Button>
                        <Button as="button" onClick={upload} disabled={!photo}>
                            <StyledActionLabel>Done</StyledActionLabel>
                        </Button>
                    </RowContainerBottom>
                </>
            )}

            {!error && !photo && (
                <RowContainerBottom>
                    <Button onClick={takePhoto} disabled={!stream}>
                        Take photo
                    </Button>
                </RowContainerBottom>
            )}

            <canvas ref={canvasRef} style={{ display: 'none' }} />
        </ImageContainer>
    )
}

const StyledActionLabel = styled(Typography)`
    ${baseFont}
    color: white;

    &:hover {
        color: white;
        background: #007079;
    }
`

const VideoView = styled.video`
    display: block;
    width: 100%;
    max-width: 100%;
    max-height: 40vh;
    height: auto;
    object-fit: contain;
    border-radius: 4px;
`

const ImageContainer = styled(Dialog)`
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 550px;
    max-width: calc(100% - 32px);
    padding: 16px;
    max-height: 80vh;
    overflow: auto;
    box-sizing: border-box;

    @media (max-width: 1024px) {
        overflow: auto;
    }
`

const ImageView = styled.img`
    display: block;
    width: 100%;
    max-width: 100%;
    max-height: 40vh;
    height: auto;
    object-fit: contain;
    border-radius: 4px;
`

const ErrorText = styled(Typography)`
    color: #b30d2f;
    margin: 8px 0 12px;
`

const HeaderLabel = styled(Typography)`
    font-family: Equinor;
    font-size: 16px;
    font-weight: 500;
    line-height: 24px;
    letter-spacing: 0.2px;
    color: #3d3d3d;
`

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    margin-bottom: 12px;
    gap: 12px;
`

const CloseButton = styled(Button)`
    ${baseFont}
    color: #007079;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px;
    text-decoration: none;
    background: none;
    border: none;
    cursor: pointer;
    flex-shrink: 0;

    &:hover {
        background: white;
    }

    @media (max-width: 1024px) {
        color: #007079;

        &:hover {
            background: white;
        }
    }
`

const RowContainerBottom = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    width: 100%;
    padding: 8px 0 0;
    margin-bottom: 0;
    gap: 16px;
    box-sizing: border-box;
`

const Label = styled.span`
    font-family: Equinor;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
`

export default TakePhotoOnboarding
